// components/Survey/QuestionForm.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, QuestionMarkCircleIcon, XMarkIcon, Bars3Icon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import { surveyService } from '../../services/surveyService';
import { surveyCategoryService } from '../../services/surveyCategoryService';

const QuestionForm = ({ surveyId, onSubmit, onCancel, initialData, isEditing = false }) => {
  const [formData, setFormData] = useState({
    questionText: '',
    type: 1,
    categoryId: '',
    order: 0,
    isRequired: false,
    placeholder: '',
    helpText: '',
    optionsJson: '[]',
    settingsJson: '{}',
    validationRulesJson: '{}',
    styleJson: '{}',
    minValue: 1,
    maxValue: 5,
    defaultValue: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ratingLabels, setRatingLabels] = useState(['Very Poor', 'Poor', 'Average', 'Good', 'Excellent']);
  const [options, setOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [selectedColor, setSelectedColor] = useState('#3b82f6');
  const [surveys, setSurveys] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState(surveyId || '');
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const questionTypes = [
    { id: 1, name: 'Rating Scale (1-5)', icon: '⭐', description: '1-5 star rating scale' },
    { id: 2, name: 'Text Field', icon: '📝', description: 'Single line text input' },
    { id: 3, name: 'Date Field', icon: '📅', description: 'Date picker' },
    { id: 4, name: 'Text Area', icon: '📄', description: 'Multi-line text input' },
    { id: 5, name: 'Radio Buttons', icon: '🔘', description: 'Single choice from options' },
    { id: 6, name: 'Hidden Field', icon: '👻', description: 'Hidden input field' },
    { id: 7, name: 'Number Field', icon: '🔢', description: 'Numeric input' },
    { id: 8, name: 'Email Field', icon: '📧', description: 'Email input with validation' },
    { id: 9, name: 'Checkboxes', icon: '☑️', description: 'Multiple choice checkboxes' },
    { id: 10, name: 'Select List', icon: '📋', description: 'Dropdown select' },
    { id: 11, name: 'File Upload', icon: '📎', description: 'File upload field' },
    { id: 12, name: 'Net Promoter Score', icon: '💯', description: 'NPS scale 0-10' },
    { id: 13, name: 'Signature', icon: '✍️', description: 'Signature capture' },
    { id: 14, name: 'Paragraph', icon: '📖', description: 'Read-only text paragraph' }
  ];

  const colorPalette = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', 
    '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7'
  ];

  useEffect(() => {
    loadSurveys();
    if (isEditing && initialData) {
      // Safely set form data with proper number handling
      const questionTypeMap = {
        Heading: 0,
        RatingScale: 1,
        TextField: 2,
        DateField: 3,
        TextArea: 4,
        RadioButtons: 5,
        HiddenField: 6,
        NumberField: 7,
        EmailField: 8,
        Checkboxes: 9,
        SelectList: 10,
        FileUpload: 11,
        NetPromoterScore: 12,
        Signature: 13,
        Paragraph: 14
      };
      const normalizedType = typeof initialData.type === 'number'
        ? initialData.type
        : (questionTypeMap[initialData.type] ?? (parseInt(initialData.type, 10) || 1));

      const safeInitialData = {
        ...initialData,
        type: normalizedType,
        minValue: initialData.minValue ?? null,
        maxValue: initialData.maxValue ?? null,
        order: initialData.order ?? 0,
        questionText: initialData.questionText || '',
        helpText: initialData.helpText || '',
        placeholder: initialData.placeholder || '',
        categoryId: initialData.categoryId || ''
      };
      setFormData(safeInitialData);
      
      // GET /SurveyQuestions returns Options as string[]. Older callers may
      // still provide optionsJson. Normalize both shapes for this form.
      try {
        let parsedOptions = [];
        if (Array.isArray(initialData.options)) {
          parsedOptions = initialData.options.map((option, index) => ({
            id: option?.id ?? `${Date.now()}-${index}`,
            label: typeof option === 'string' ? option : (option?.label ?? '')
          }));
        } else if (initialData.optionsJson) {
          const parsed = typeof initialData.optionsJson === 'string'
            ? JSON.parse(initialData.optionsJson)
            : initialData.optionsJson;
          parsedOptions = (Array.isArray(parsed) ? parsed : []).map((option, index) => ({
            id: option?.id ?? `${Date.now()}-${index}`,
            label: typeof option === 'string' ? option : (option?.label ?? '')
          }));
        }
        setOptions(parsedOptions);
        setFormData(prev => ({
          ...prev,
          optionsJson: JSON.stringify(parsedOptions.map(option => option.label))
        }));
      } catch (e) {
        console.error('Error parsing options:', e);
        setOptions([]);
        setFormData(prev => ({ ...prev, optionsJson: '[]' }));
      }

      // Prefer the surveyId already supplied by QuestionsList. This avoids
      // calling the nonexistent getAllCategories() method while editing.
      const effectiveSurveyId = initialData.surveyId || surveyId;
      if (effectiveSurveyId) {
        setSelectedSurveyId(effectiveSurveyId);
        loadCategoriesForSurvey(effectiveSurveyId);
      }
    } else if (surveyId) {
      setSelectedSurveyId(surveyId);
      loadCategoriesForSurvey(surveyId);
    }
  }, [initialData, isEditing, surveyId]);

  const loadSurveys = async () => {
    try {
      setLoadingSurveys(true);
      const data = await surveyService.getSurveys();
      setSurveys(data);
    } catch (err) {
      console.error('Error loading surveys:', err);
      setErrors(prev => ({ ...prev, surveys: 'Failed to load surveys' }));
    } finally {
      setLoadingSurveys(false);
    }
  };

  const loadCategoriesForSurvey = async (surveyId) => {
    try {
      setLoadingCategories(true);
      const data = await surveyCategoryService.getSurveyCategories(surveyId);
      setCategories(data);
      setSelectedSurveyId(surveyId);
    } catch (err) {
      console.error('Error loading categories:', err);
      setErrors(prev => ({ ...prev, categories: 'Failed to load categories' }));
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSurveyChange = (e) => {
    const surveyId = e.target.value;
    setSelectedSurveyId(surveyId);
    setFormData(prev => ({ ...prev, categoryId: '' }));
    setCategories([]);
    
    if (surveyId) {
      loadCategoriesForSurvey(surveyId);
    }
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setFormData(prev => ({ ...prev, categoryId }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue;
    if (type === 'checkbox') {
      processedValue = checked;
    } else if (type === 'number') {
      if (value === '') {
        processedValue = null;
      } else {
        const numValue = parseInt(value);
        processedValue = isNaN(numValue) ? null : numValue;
      }
    } else {
      processedValue = value;
    }
    
    setFormData(prev => ({ 
      ...prev, 
      [name]: processedValue
    }));
    
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTextAreaChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleRatingLabelChange = (index, value) => {
    const newLabels = [...ratingLabels];
    newLabels[index] = value;
    setRatingLabels(newLabels);
    setFormData(prev => ({ 
      ...prev, 
      settingsJson: JSON.stringify({ ...JSON.parse(prev.settingsJson || '{}'), ratingLabels: newLabels })
    }));
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      const newOptions = [...options, { id: Date.now(), label: newOption.trim() }];
      setOptions(newOptions);
      setFormData(prev => ({
        ...prev,
        optionsJson: JSON.stringify(newOptions.map(option => option.label))
      }));
      setNewOption('');
    }
  };

  const handleRemoveOption = (id) => {
    const newOptions = options.filter(opt => opt.id !== id);
    setOptions(newOptions);
    setFormData(prev => ({
      ...prev,
      optionsJson: JSON.stringify(newOptions.map(option => option.label))
    }));
  };

  const handleReorderOptions = (fromIndex, toIndex) => {
    const newOptions = [...options];
    const [movedItem] = newOptions.splice(fromIndex, 1);
    newOptions.splice(toIndex, 0, movedItem);
    setOptions(newOptions);
    setFormData(prev => ({
      ...prev,
      optionsJson: JSON.stringify(newOptions.map(option => option.label))
    }));
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    setFormData(prev => ({ 
      ...prev, 
      styleJson: JSON.stringify({ ...JSON.parse(prev.styleJson || '{}'), primaryColor: color })
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.categoryId) newErrors.categoryId = 'Please select a category';
    if (!formData.questionText.trim()) newErrors.questionText = 'Question text is required';
    
    if (formData.type === 1) {
      const min = formData.minValue || 1;
      const max = formData.maxValue || 5;
      if (min >= max) {
        newErrors.range = 'Max value must be greater than min value';
      }
    }
    
    if ((formData.type === 5 || formData.type === 9 || formData.type === 10) && options.length === 0) {
      newErrors.options = 'At least one option is required for this question type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        surveyId: parseInt(selectedSurveyId),
        minValue: formData.minValue || null,
        maxValue: formData.maxValue || null,
        order: formData.order || 0
      };
      
      await onSubmit(submitData);
    } catch (error) {
      setErrors({ general: error.message || 'Error saving question' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTypeSpecificFields = () => {
    switch (formData.type) {
      case 1: // Rating Scale
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Value</label>
                <input
                  type="number"
                  name="minValue"
                  value={formData.minValue || 1}
                  onChange={handleChange}
                  min="1"
                  max="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Value</label>
                <input
                  type="number"
                  name="maxValue"
                  value={formData.maxValue || 5}
                  onChange={handleChange}
                  min="2"
                  max="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {errors.range && (
              <p className="text-red-500 text-sm">{errors.range}</p>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rating Labels</label>
              <div className="space-y-2">
                {ratingLabels.map((label, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-blue-500 text-white rounded-full text-sm font-medium">
                      {index + 1}
                    </span>
                    <input
                      type="text"
                      value={label}
                      onChange={(e) => handleRatingLabelChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder={`Label for ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Color Theme</label>
              <div className="flex flex-wrap gap-2">
                {colorPalette.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleColorSelect(color)}
                    className={`w-8 h-8 rounded-full border-2 ${
                      selectedColor === color ? 'border-gray-800' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case 2: // Text Field
      case 8: // Email Field
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Placeholder Text</label>
              <input
                type="text"
                name="placeholder"
                value={formData.placeholder || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter placeholder text..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Length</label>
                <input
                  type="number"
                  name="maxValue"
                  value={formData.maxValue || ''}
                  onChange={handleChange}
                  min="1"
                  max="1000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Maximum characters"
                />
            </div>
          </div>
        );

      case 5: // Radio Buttons
      case 9: // Checkboxes
      case 10: // Select List
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
              <div className="space-y-2 mb-3">
                {options.map((option, index) => (
                  <motion.div
                    key={option.id}
                    layout
                    className="flex items-center space-x-2 p-2 bg-blue-50 rounded-lg"
                  >
                    <Bars3Icon className="w-4 h-4 text-blue-400 cursor-grab" />
                    <span className="flex-1">{option.label}</span>
                    <div className="flex gap-1">
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => handleReorderOptions(index, index - 1)}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded"
                        >
                          ↑
                        </button>
                      )}
                      {index < options.length - 1 && (
                        <button
                          type="button"
                          onClick={() => handleReorderOptions(index, index + 1)}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded"
                        >
                          ↓
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(option.id)}
                        className="p-1 text-red-500 hover:bg-red-100 rounded"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add new option..."
                />
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Add
                </button>
              </div>
              {errors.options && (
                <p className="text-red-500 text-sm mt-1">{errors.options}</p>
              )}
            </div>
          </div>
        );

      case 7: // Number Field
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Min Value</label>
              <input
                type="number"
                name="minValue"
                value={formData.minValue || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Value</label>
              <input
                type="number"
                name="maxValue"
                value={formData.maxValue || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 12: // Net Promoter Score
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                NPS questions use a 0-10 scale with labels: Detractors (0-6), Passives (7-8), Promoters (9-10)
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-pink-50 via-white to-pink-50 p-6 rounded-2xl shadow-md">
      {/* Left Panel - Question Types */}
      <div className="w-full lg:w-1/3 bg-gradient-to-br from-pink-100 via-white to-pink-100 rounded-2xl shadow-md p-6 border border-pink-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Question Types</h2>
        <div className="space-y-2 h-full">
          {questionTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, type: type.id }))}
              className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                formData.type === type.id
                  ? 'bg-pink-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-pink-50 border border-gray-200'
              }`}
            >
              <div className="flex items-center space-x-3">
                <span className="text-xl">{type.icon}</span>
                <div>
                  <div className="font-medium">{type.name}</div>
                  <div className={`text-sm ${
                    formData.type === type.id ? 'text-pink-100' : 'text-gray-500'
                  }`}>
                    {type.description}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 bg-gradient-to-br from-pink-50 via-white to-pink-50 rounded-xl shadow-sm p-6 border border-pink-100">
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-6"
        >
          {errors.general && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Survey and Category Selection */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Survey *
              </label>
              <select
                value={selectedSurveyId}
                onChange={handleSurveyChange}
                disabled={loadingSurveys || !!surveyId}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 bg-white ${
                  loadingSurveys ? 'opacity-50' : ''
                } ${surveyId ? 'bg-gray-100' : ''}`}
              >
                <option value="">Select a Survey</option>
                {surveys.map((survey) => (
                  <option key={survey.id} value={survey.id}>
                    {survey.name}
                  </option>
                ))}
              </select>
              {loadingSurveys && (
                <p className="text-gray-500 text-sm mt-1">Loading surveys...</p>
              )}
              {surveyId && (
                <p className="text-pink-600 text-sm mt-1">Survey is pre-selected</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleCategoryChange}
                disabled={loadingCategories || !selectedSurveyId}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 bg-white ${
                  errors.categoryId ? 'border-red-500' : ''
                } ${loadingCategories || !selectedSurveyId ? 'opacity-50' : ''}`}
              >
                <option value="">Select a Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {loadingCategories && (
                <p className="text-gray-500 text-sm mt-1">Loading categories...</p>
              )}
              {!selectedSurveyId && !loadingCategories && (
                <p className="text-gray-500 text-sm mt-1">Please select a survey first</p>
              )}
              {errors.categoryId && (
                <p className="text-red-500 text-sm mt-1">{errors.categoryId}</p>
              )}
            </div>
          </div>

          {/* Question Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Question Text *
            </label>
            <textarea
              name="questionText"
              value={formData.questionText || ''}
              onChange={(e) => handleTextAreaChange('questionText', e.target.value)}
              required
              rows={3}
              placeholder="Enter your question here..."
              className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-pink-400 outline-none shadow-sm text-gray-800 resize-none ${
                errors.questionText ? 'border-red-500' : ''
              }`}
            />
            {errors.questionText && (
              <p className="text-red-500 text-sm mt-1">{errors.questionText}</p>
            )}
          </div>

          {/* Type-Specific Fields */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Question Settings</h3>
            {renderTypeSpecificFields()}
          </div>

          {/* Common Settings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
              <input
                type="number"
                name="order"
                value={formData.order || 0}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
              />
            </div>
            
            <div className="flex items-center space-x-3 p-4 bg-pink-50 rounded-lg">
              <input
                type="checkbox"
                name="isRequired"
                checked={formData.isRequired}
                onChange={handleChange}
                className="w-4 h-4 text-pink-600 rounded border-gray-300 focus:ring-pink-400"
              />
              <label className="text-sm font-medium text-gray-700">Required Question</label>
            </div>
          </div>

          {/* Help Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Help Text</label>
            <textarea
              name="helpText"
              value={formData.helpText || ''}
              onChange={(e) => handleTextAreaChange('helpText', e.target.value)}
              rows={2}
              placeholder="Additional instructions or help text..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 resize-none"
            />
          </div>

          {/* Quick Tips */}
          <div className="mb-6 bg-pink-50 border border-pink-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-pink-800 mb-2">
              💡 Question Creation Tips
            </h4>
            <ul className="text-pink-700 text-sm space-y-1">
              <li>• Select a survey and category first</li>
              <li>• Write clear, concise question text</li>
              <li>• Choose the appropriate question type</li>
              <li>• Add options for multiple choice questions</li>
              <li>• Set appropriate validation rules</li>
            </ul>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  {isEditing ? 'Update Question' : 'Create Question'}
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default QuestionForm;